import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ApiService } from '../../../../../services/api.service';
import { FileUploadComponent } from '../../../../../common/file-upload/file-upload.component';
import { ConfigurazioneAccreditamenti, DocumentoAccredito, Evento, Persona, Subappalto } from '../../../../../shared/interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { CheckboxChangeEvent } from 'primeng/checkbox';

@Component({
  selector: 'app-edit-personale-accrediti-backoffice',
  templateUrl: './edit-personale-accrediti-backoffice.component.html',
  styleUrls: ['./edit-personale-accrediti-backoffice.component.css']
})
export class EditPersonaleAccreditiBackofficeComponent implements OnInit {

  constructor(private apiService: ApiService) {}

  @ViewChild('uploadCertificazioni', {static: false}) uploadCertificazioni!: FileUploadComponent;
  @ViewChild('uploadUnilav', {static: false}) uploadUnilav!: FileUploadComponent;

  @Input() visible: boolean = false;
  @Input() persona?: Persona;
  @Input() evento?: Evento;
  @Input() subappalto?: number;
  @Input() subappalti: Subappalto[] = [];

  @Output() close = new EventEmitter<boolean>();

  get dittaOptions(): { label: string; value: number | null }[] {
    return [
      { label: 'Compagnia principale', value: null },
      ...(this.subappalti || []).map(s => ({ label: s.ragione_sociale, value: s.id })),
    ];
  }

  categorie: { categoria: string; ruoli: { ruolo: string }[] }[] = [
    {
      categoria: 'Tecnico / Operativo',
      ruoli: [
        { ruolo: 'tecnico audio' },
        { ruolo: 'tecnico luci' },
        { ruolo: 'tecnico video' },
        { ruolo: 'macchinista' },
        { ruolo: 'facchino' },
        { ruolo: 'backliner'},
        { ruolo: 'rigger / lavori in quota' },
      ],
    },
    {
      categoria: 'Artistico',
      ruoli: [
        { ruolo: 'attore / attrice' },
        { ruolo: 'performer' },
        { ruolo: 'cantante' },
        { ruolo: 'musicista' },
      ],
    },
    {
      categoria: 'Produzione / Organizzazione',
      ruoli: [
        { ruolo: 'direzione di produzione' },
        { ruolo: 'assistente di produzione' },
        { ruolo: 'tour manager' },
        { ruolo: 'sarta / costumista' },
        { ruolo: 'make up Artist / parrucchiere' },        
        { ruolo: 'altro ruolo organizzativo' },
      ],
    },
    { categoria: 'Altro', ruoli: [] },
  ];  
  ruoli_filtrati: { ruolo: string }[] = [];
  tipologie: { tipologia: string }[] = [
    { tipologia: 'Dipendente della Compagnia / Ditta' },
    { tipologia: 'Lavoratore Autonomo con P.IVA' },
    { tipologia: 'Artista con agibilità INPS' },
    { tipologia: 'SubAppalto' },
    { tipologia: 'Altro' },
  ];
  presenze: { presenza: string }[] = [
    { presenza: 'Sempre' },
    { presenza: 'Montaggio' },
    { presenza: 'Prove' },
    { presenza: 'Spettacolo' },
    { presenza: 'Smontaggio' },
  ];
  presenze_filtrate: { presenza: string }[] = [];

  loading: boolean = false;  
  unilav: boolean = true;
  certificazioni: boolean = false;
  configurazione?: ConfigurazioneAccreditamenti;

  formPersonale!: FormGroup;

  ngOnInit(): void {

    this.formPersonale = new FormGroup({
      nome: new FormControl(null, Validators.required),
      cognome: new FormControl(null, Validators.required),
      categoria: new FormControl(null, Validators.required),
      ruolo: new FormControl(null, Validators.required),
      tipologia: new FormControl(null, Validators.required),
      presenza: new FormControl(null, Validators.required),
      id_subappalto: new FormControl(null),
      certificazioni: new FormControl([]),
      agibilita_inps: new FormControl(null),
      unilav: new FormControl([])
    });

    this.apiService.getConfigurazioneAccreditamenti().subscribe({
      next: (c) => this.configurazione = c,
      error: () => {},
    });
      
    if (this.persona) {

      this.ruoli_filtrati = [...this.categorie].filter((cat) => cat.categoria ==  this.persona?.categoria)[0].ruoli;      
      
      this.formPersonale.patchValue({
        nome: this.persona?.nome,
        cognome: this.persona?.cognome,
        categoria: {categoria: this.persona?.categoria, ruoli: this.ruoli_filtrati},
        ruolo: {ruolo: this.persona?.ruolo},
        tipologia: {tipologia: this.persona?.tipologia},
        presenza: this.persona?.presenza ? this.persona.presenza.split(',').map(presenza => {return {presenza:presenza}; }) : null,
        agibilita_inps: this.hasInpsFlag() ? ['agibilita_inps'] : null,
        id_subappalto: this.persona?.id_subappalto || this.subappalto || null,
      });

      this.certificazioni = (this.persona?.ruolo || '') === 'rigger / lavori in quota';
      this.syncTipologiaValidators(this.persona?.tipologia);
      this.syncUnilavValidators();
    }
  }

  private hasInpsFlag(): boolean {
    const s = (this.persona?.inps ?? '').toString().trim();
    return s.length > 0 && s !== '0' && s !== 'false' && s !== 'null';
  }

  private hasExistingUnilav(): boolean {
    return this.countDocs('unilav') > 0;
  }

  private isUnilavExemptRuolo(ruolo?: string | null): boolean {
    const r = (ruolo || this.formPersonale?.get('ruolo')?.value?.ruolo || '').toLowerCase().trim();
    return ['direzione di produzione', 'tour manager', 'assistente di produzione', 'altro ruolo organizzativo'].includes(r);
  }

  private hasAgibilitaChecked(): boolean {
    const v = this.formPersonale?.get('agibilita_inps')?.value;
    return Array.isArray(v) ? v.length > 0 : !!v;
  }

  /** UniLav obbligatorio solo se non c’è già un file, non c’è agibilità e il ruolo non è esente. */
  private syncUnilavValidators() {
    const need = !this.hasAgibilitaChecked() && !this.isUnilavExemptRuolo() && !this.hasExistingUnilav();
    this.unilav = need;
    if (need) this.formPersonale.get('unilav')?.addValidators(Validators.required);
    else this.formPersonale.get('unilav')?.removeValidators(Validators.required);
    this.formPersonale.get('unilav')?.updateValueAndValidity();
  }

  private syncTipologiaValidators(tipologia?: string | null) {
    const t = tipologia || this.formPersonale?.get('tipologia')?.value?.tipologia;
    // UniLav già caricato vale come alternativa: non costringere a spuntare agibilità a falso
    if (t === 'Artista con agibilità INPS' && !this.hasExistingUnilav()) {
      this.formPersonale.get('agibilita_inps')?.setValidators(Validators.required);
    } else {
      this.formPersonale.get('agibilita_inps')?.removeValidators(Validators.required);
    }
    this.formPersonale.get('agibilita_inps')?.updateValueAndValidity();
  }  

  ngOnChanges(changes: SimpleChanges): void {
    this.persona = changes['persona']?.currentValue || this.persona;
    this.subappalto = changes['subappalto']?.currentValue || this.subappalto;
  }

  onCategoria(categoria: { categoria: string } | null) {
    if (categoria && categoria?.categoria) {

      this.formPersonale.patchValue({ ruolo: null });
      this.formPersonale.get('ruolo')?.enable();
            
    } else {
      this.formPersonale.get('ruolo')?.disable();
    }
        
  }

  onFilterRuoli(event: AutoCompleteCompleteEvent) {   
    if (this.formPersonale.get('categoria')?.value.ruoli) { 
      this.ruoli_filtrati = [...this.formPersonale.get('categoria')?.value.ruoli].filter((ruolo) => ruolo.ruolo.toLowerCase().indexOf(event.query.toLowerCase()) == 0).length <= 0 ? [{ ruolo: event.query }] : [...this.formPersonale.get('categoria')?.value.ruoli].filter((ruolo) => ruolo.ruolo.toLowerCase().indexOf(event.query.toLowerCase()) == 0);    
    } else {      
      this.ruoli_filtrati = [...this.categorie].filter((cat) => cat.categoria ==  this.formPersonale.get('categoria')?.value.categoria)[0].ruoli.length <= 0 ? [{ ruolo: event.query }] : [...this.categorie].filter((cat) => cat.categoria ==  this.formPersonale.get('categoria')?.value.categoria)[0].ruoli;    
    }
  }

  onRuolo(event: AutoCompleteSelectEvent) {
    if (event.value && event.value.ruolo == 'rigger / lavori in quota') {
      this.certificazioni = true;
      this.formPersonale.get('certificazioni')?.setValidators(Validators.required);
    } else {
      this.certificazioni = false;
      this.formPersonale.get('certificazioni')?.removeValidators(Validators.required);
    }
    this.formPersonale.get('certificazioni')?.updateValueAndValidity();
    this.syncUnilavValidators();
  }

  onFilterPresenze(event: AutoCompleteCompleteEvent) {
    this.presenze_filtrate = [...this.presenze].filter((presenza) => presenza.presenza.toLowerCase().indexOf(event.query.toLowerCase()) == 0).length <= 0 ? [{ presenza: event.query }] : [...this.presenze].filter((presenza) => presenza.presenza.toLowerCase().indexOf(event.query.toLowerCase()) == 0);
  }

  onTipologia(event: SelectButtonChangeEvent) {
    this.syncTipologiaValidators(event.value?.tipologia);
  }

  onAgibilita(_event: CheckboxChangeEvent) {
    this.syncUnilavValidators();
  }

  onClose() {
    this.visible = false;
    this.close.emit(this.visible);
  }

  onDocumento(documento: string) {
    this.apiService.openProtectedFile(documento);
  }

  countDocs(tipo: string): number {
    return (this.persona?.documenti || []).filter(d => d.tipo === tipo).length;
  }

  docsByTipo(tipo: string) {
    return (this.persona?.documenti || []).filter(d => d.tipo === tipo);
  }

  onEdit() {

    let persona = new FormData();

    persona.append("id_evento", (this.persona?.id_evento || 0).toString());
    persona.append("nome", this.formPersonale.get('nome')?.value || '');
    persona.append("cognome", this.formPersonale.get('cognome')?.value || '');
    persona.append("anagrafica", (this.formPersonale.get('cognome')?.value || '') + ' ' + (this.formPersonale.get('nome')?.value || '') + (this.formPersonale.get('ruolo')?.value ? ' - ' + this.formPersonale.get('ruolo')?.value.ruolo : ''));
    persona.append("categoria", this.formPersonale.get('categoria')?.value ? this.formPersonale.get('categoria')?.value.categoria : '');
    persona.append("ruolo", this.formPersonale.get('ruolo')?.value ? this.formPersonale.get('ruolo')?.value.ruolo : '');
    persona.append("tipologia", this.formPersonale.get('tipologia')?.value ? this.formPersonale.get('tipologia')?.value.tipologia: '');
    persona.append("presenza", (this.formPersonale.get('presenza')?.value|| []).map((presenza: {presenza: string}) => presenza.presenza).join(','));
    persona.append("agibilita_inps", this.formPersonale.get('agibilita_inps')?.value || '');
    const idSub = this.formPersonale.get('id_subappalto')?.value ?? this.subappalto;
    if (idSub) persona.append("id_subappalto", idSub.toString());
    else persona.append("id_subappalto", '');
    (this.formPersonale.get('certificazioni')?.value || []).map((file: File) => persona.append("certificazioni", file || ''));    
    (this.formPersonale.get('unilav')?.value || []).map((file: File) => persona.append("unilav", file || ''));
          
    this.apiService.updatePersonale(persona, this.persona!.id).subscribe({
      next: (data) => {
        this.apiService.setPersonaleEvento(this.persona!.id_evento)
        this.apiService.setSubappaltiEvento(this.persona!.id_evento)
      },
      error: (err) => this.apiService.message('error', 'Errore', err.error.message.message),
      complete: () => {
        this.uploadCertificazioni.clearUpload();
        this.uploadUnilav.clearUpload();

        this.formPersonale.reset();
        this.formPersonale.markAsUntouched();
        this.formPersonale.markAsPristine();    

        this.onClose()
      }
    });

  }
}
